import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { analyticsApi, ccApi, reportApi, sosApi } from '../services/api';
import { PHASE_META, PLATOON_PHASES, type ExerciseEvent } from '../types/domain';
import { REPORT_TYPES, REPORT_TYPE_META } from '../lib/reports';
import type { FieldReport } from '../store/reportStore';
import { format, subDays } from 'date-fns';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  RadialLinearScale,
} from 'chart.js';
import { Bar, Doughnut, Pie, PolarArea } from 'react-chartjs-2';
import clsx from 'clsx';
import {
  ChartBarIcon,
  MapIcon,
  ClockIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  RadialLinearScale
);

type RecordCounts = Record<string, number>;

function toLabelsValues(rec: RecordCounts | undefined | null) {
  const entries = Object.entries(rec || {}).sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0));
  return {
    labels: entries.map(([k]) => k),
    values: entries.map(([, v]) => Number(v || 0)),
  };
}

function pickColors(count: number) {
  const palette = [
    '#22c55e', // green
    '#38bdf8', // sky
    '#a78bfa', // violet
    '#f59e0b', // amber
    '#fb7185', // rose
    '#f97316', // orange
    '#60a5fa', // blue
    '#34d399', // emerald
    '#f472b6', // pink
    '#e879f9', // fuchsia
  ];
  const out: string[] = [];
  for (let i = 0; i < count; i++) out.push(palette[i % palette.length]);
  return out;
}

function withAlpha(hex: string, alpha: number) {
  const h = hex.replace('#', '').trim();
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export default function AnalyticsPage() {
  const [dateRange, setDateRange] = useState({
    startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  });

  const { data: sosReport } = useQuery({
    queryKey: ['sos-report', dateRange],
    queryFn: () => analyticsApi.getSOSReport({
      startDate: new Date(dateRange.startDate).toISOString(),
      endDate: new Date(dateRange.endDate).toISOString(),
    }),
    refetchInterval: 10000, // Auto-refresh every 10 seconds
  });

  const { data: movementData } = useQuery({
    queryKey: ['movement-summary', dateRange],
    queryFn: () => analyticsApi.getMovementSummary({
      startDate: new Date(dateRange.startDate).toISOString(),
      endDate: new Date(dateRange.endDate).toISOString(),
    }),
    refetchInterval: 10000, // Auto-refresh every 10 seconds
  });

  const sos = sosReport?.data;
  const movement = movementData?.data;

  // ── Analytics v2 (P8): time-in-phase, report volume, SOS response latency ──
  const startIso = new Date(dateRange.startDate).toISOString();
  const endIso = new Date(`${dateRange.endDate}T23:59:59`).toISOString();

  const phaseFeedQ = useQuery({
    queryKey: ['analytics-phase-feed', dateRange],
    queryFn: async () =>
      (await ccApi.getFeed({ type: 'PHASE_TAG', since: startIso, until: endIso, limit: 500 })).data.events as ExerciseEvent[],
    retry: false,
  });

  const reportsQ = useQuery({
    queryKey: ['analytics-reports', dateRange],
    queryFn: async () =>
      (await reportApi.getAll({ since: startIso, until: endIso, limit: 200 })).data?.data?.reports as FieldReport[],
    retry: false,
  });

  const sosHistQ = useQuery({
    queryKey: ['analytics-sos-hist', dateRange],
    queryFn: async () => (await sosApi.getHistory({ startDate: startIso, endDate: endIso })).data.sosEvents as any[],
    retry: false,
  });

  // Time-in-phase per platoon (minutes): pair consecutive PHASE_TAG events; each
  // interval counts toward the phase set at its start event (capped at 6h to
  // ignore overnight gaps). Trailing open phase is not counted (no end marker).
  const CAP_MS = 6 * 60 * 60 * 1000;
  const timeInPhase = useMemo(() => {
    const events = phaseFeedQ.data ?? [];
    const metaPhase = (m: ExerciseEvent['metadata']): string | null => {
      if (!m) return null;
      const obj = typeof m === 'string' ? (() => { try { return JSON.parse(m); } catch { return null; } })() : m;
      return obj && typeof obj.phase === 'string' ? obj.phase : null;
    };
    const byPlatoon = new Map<string, ExerciseEvent[]>();
    events.forEach((e) => {
      const key = e.platoon?.callsign || e.platoonId || 'Unknown';
      if (!byPlatoon.has(key)) byPlatoon.set(key, []);
      byPlatoon.get(key)!.push(e);
    });
    const perPlatoon: { callsign: string; minutes: Record<string, number> }[] = [];
    byPlatoon.forEach((evs, callsign) => {
      const ordered = [...evs].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      const minutes: Record<string, number> = {};
      for (let i = 0; i < ordered.length - 1; i++) {
        const phase = metaPhase(ordered[i].metadata);
        if (!phase) continue;
        const dt = Math.min(CAP_MS, new Date(ordered[i + 1].timestamp).getTime() - new Date(ordered[i].timestamp).getTime());
        if (dt > 0) minutes[phase] = (minutes[phase] ?? 0) + dt / 60000;
      }
      if (Object.keys(minutes).length > 0) perPlatoon.push({ callsign, minutes });
    });
    return perPlatoon;
  }, [phaseFeedQ.data]);

  const phaseChart = useMemo(() => ({
    labels: timeInPhase.map((p) => p.callsign),
    datasets: PLATOON_PHASES.filter((ph) => timeInPhase.some((p) => (p.minutes[ph] ?? 0) > 0)).map((ph) => ({
      label: PHASE_META[ph].label,
      data: timeInPhase.map((p) => Math.round(p.minutes[ph] ?? 0)),
      backgroundColor: PHASE_META[ph].color,
      borderColor: PHASE_META[ph].color,
      borderWidth: 0,
    })),
  }), [timeInPhase]);

  // Report volume by type + by hour-of-day.
  const reportVolume = useMemo(() => {
    const reports = reportsQ.data ?? [];
    const byType: Record<string, number> = {};
    const byHour: number[] = new Array(24).fill(0);
    reports.forEach((r) => {
      byType[r.type] = (byType[r.type] ?? 0) + 1;
      const h = new Date(r.createdAt).getHours();
      if (!Number.isNaN(h)) byHour[h] += 1;
    });
    return { byType, byHour, total: reports.length };
  }, [reportsQ.data]);

  // SOS response latency (minutes): triggered→acknowledged and triggered→resolved.
  const sosLatency = useMemo(() => {
    const evs = sosHistQ.data ?? [];
    const ackMins: number[] = [];
    const resolveMins: number[] = [];
    evs.forEach((e) => {
      const t = new Date(e.triggeredAt).getTime();
      if (e.acknowledgedAt) ackMins.push((new Date(e.acknowledgedAt).getTime() - t) / 60000);
      if (e.resolvedAt) resolveMins.push((new Date(e.resolvedAt).getTime() - t) / 60000);
    });
    const avg = (a: number[]) => (a.length ? a.reduce((s, x) => s + x, 0) / a.length : 0);
    return {
      count: evs.length,
      ackAvg: avg(ackMins),
      ackMax: ackMins.length ? Math.max(...ackMins) : 0,
      resolveAvg: avg(resolveMins),
      recent: evs.slice(0, 12).map((e: any) => ({
        callsign: e.platoon?.callsign ?? '—',
        ack: e.acknowledgedAt ? (new Date(e.acknowledgedAt).getTime() - new Date(e.triggeredAt).getTime()) / 60000 : 0,
      })),
    };
  }, [sosHistQ.data]);

  const sosStatus = toLabelsValues(sos?.summary?.byStatus as RecordCounts | undefined);
  const sosTriggers = toLabelsValues(sos?.summary?.byTriggerType as RecordCounts | undefined);
  const sosByUnit = toLabelsValues(sos?.summary?.byUnitId as RecordCounts | undefined);

  const movementMinutes = {
    moving: Number(movement?.summary?.totalMovementMinutes || 0),
    stationary: Number(movement?.summary?.totalStationaryMinutes || 0),
  };
  const movementTotalMin = movementMinutes.moving + movementMinutes.stationary;
  const movementSplit = {
    labels: ['Moving', 'Stationary'],
    values: [movementMinutes.moving, movementMinutes.stationary],
  };

  const sosStatusColors = pickColors(sosStatus.labels.length);
  const sosTriggerColors = pickColors(sosTriggers.labels.length);
  const sosUnitColors = pickColors(sosByUnit.labels.length);

  const chartBaseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: '#cbd5e1',
          boxWidth: 10,
          boxHeight: 10,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(2,6,23,0.92)',
        titleColor: '#e2e8f0',
        bodyColor: '#e2e8f0',
        borderColor: 'rgba(148,163,184,0.15)',
        borderWidth: 1,
      },
    },
  } as const;

  const handleExport = async (type: string, format: 'json' | 'csv') => {
    try {
      const response = await analyticsApi.exportData(
        type,
        format,
        new Date(dateRange.startDate).toISOString(),
        new Date(dateRange.endDate).toISOString()
      );
      
      const blob = format === 'csv' 
        ? new Blob([response.data], { type: 'text/csv' })
        : new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-export-${dateRange.startDate}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-display font-bold text-gray-100">Analytics</h1>
          <p className="text-gray-500 text-sm">Operational insights and reports</p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              aria-label="Start date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="px-2 py-1.5 min-h-11 lg:min-h-0 bg-command-panel border border-command-border rounded-lg text-gray-100 text-sm"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              aria-label="End date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="px-2 py-1.5 min-h-11 lg:min-h-0 bg-command-panel border border-command-border rounded-lg text-gray-100 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div className="p-3 rounded-xl border border-red-500/30 bg-gradient-to-br from-red-500/15 via-command-panel to-command-panel">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <ChartBarIcon className="w-5 h-5" />
            <span className="text-sm">SOS Events</span>
          </div>
          <p className="text-3xl font-bold text-red-400">{sos?.summary?.total || 0}</p>
          <p className="text-xs text-gray-500 mt-1">
            Avg Response: {sos?.responseTime?.averageSeconds || '--'}s
          </p>
        </div>

        <div className="p-3 rounded-xl border border-blue-500/30 bg-gradient-to-br from-blue-500/15 via-command-panel to-command-panel">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <MapIcon className="w-5 h-5" />
            <span className="text-sm">Distance Traveled</span>
          </div>
          <p className="text-3xl font-bold text-blue-400">
            {movement?.summary?.totalDistanceKm?.toFixed(1) || 0} km
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Avg Speed: {movement?.summary?.avgSpeedKmh || 0} km/h
          </p>
        </div>

        <div className="p-3 rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/15 via-command-panel to-command-panel">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <ClockIcon className="w-5 h-5" />
            <span className="text-sm">Active Time</span>
          </div>
          <p className="text-3xl font-bold text-green-400">
            {Math.round((movement?.summary?.totalMovementMinutes || 0) / 60)}h
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Stationary: {Math.round((movement?.summary?.totalStationaryMinutes || 0) / 60)}h
          </p>
        </div>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* SOS Analysis */}
        <div className="p-4 bg-command-panel border border-command-border rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-100">SOS Analysis</h2>
            <button
              onClick={() => handleExport('sos', 'csv')}
              className="flex items-center gap-1 text-sm text-command-accent hover:underline"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Export
            </button>
          </div>

          {sos?.summary ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="rounded-xl border border-command-border bg-command-bg p-3">
                <div className="text-xs text-gray-400 mb-2">SOS by Status (Pie)</div>
                <div className="h-[160px]">
                  <Doughnut
                    data={{
                      labels: sosStatus.labels,
                      datasets: [
                        {
                          data: sosStatus.values,
                          backgroundColor: sosStatusColors.map(c => withAlpha(c, 0.85)),
                          borderColor: sosStatusColors.map(c => withAlpha(c, 1)),
                          borderWidth: 1,
                        },
                      ],
                    }}
                    options={{
                      ...chartBaseOptions,
                      cutout: '62%',
                      plugins: { ...chartBaseOptions.plugins, legend: { ...chartBaseOptions.plugins.legend, position: 'bottom' } },
                    }}
                  />
                </div>
              </div>

              <div className="rounded-xl border border-command-border bg-command-bg p-3">
                <div className="text-xs text-gray-400 mb-2">Trigger Types (Area)</div>
                <div className="h-[160px]">
                  <PolarArea
                    data={{
                      labels: sosTriggers.labels,
                      datasets: [
                        {
                          data: sosTriggers.values,
                          backgroundColor: sosTriggerColors.map(c => withAlpha(c, 0.55)),
                          borderColor: sosTriggerColors.map(c => withAlpha(c, 0.95)),
                          borderWidth: 1,
                        },
                      ],
                    }}
                    options={{
                      ...chartBaseOptions,
                      scales: {
                        r: {
                          grid: { color: 'rgba(148,163,184,0.12)' },
                          angleLines: { color: 'rgba(148,163,184,0.12)' },
                          pointLabels: { color: '#cbd5e1', font: { size: 11 } },
                          ticks: { display: false },
                        },
                      },
                      plugins: { ...chartBaseOptions.plugins, legend: { ...chartBaseOptions.plugins.legend, position: 'bottom' } },
                    }}
                  />
                </div>
              </div>

              <div className={clsx("rounded-xl border border-command-border bg-command-bg p-3", sosByUnit.labels.length > 1 ? "" : "md:col-span-2")}>
                <div className="text-xs text-gray-400 mb-2">Response Time (Bars)</div>
                <div className="h-[160px]">
                  <Bar
                    data={{
                      labels: ['Fastest', 'Average', 'Slowest'],
                      datasets: [
                        {
                          label: 'Seconds',
                          data: [
                            sos.responseTime?.minMs ? Math.round(sos.responseTime.minMs / 1000) : 0,
                            sos.responseTime?.averageSeconds || 0,
                            sos.responseTime?.maxMs ? Math.round(sos.responseTime.maxMs / 1000) : 0,
                          ],
                          backgroundColor: [
                            withAlpha('#22c55e', 0.75),
                            withAlpha('#22d3ee', 0.75),
                            withAlpha('#fb7185', 0.75),
                          ],
                          borderColor: ['#22c55e', '#22d3ee', '#fb7185'],
                          borderWidth: 1,
                          borderRadius: 8,
                        },
                      ],
                    }}
                    options={{
                      ...chartBaseOptions,
                      plugins: { ...chartBaseOptions.plugins, legend: { display: false } },
                      scales: {
                        x: { grid: { display: false }, ticks: { color: '#cbd5e1' } },
                        y: { grid: { color: 'rgba(148,163,184,0.12)' }, ticks: { color: '#94a3b8' } },
                      },
                    }}
                  />
                </div>
              </div>

              {sosByUnit.labels.length > 1 ? (
                <div className="rounded-xl border border-command-border bg-command-bg p-3">
                  <div className="text-xs text-gray-400 mb-2">SOS by Unit (Pie)</div>
                  <div className="h-[160px]">
                    <Pie
                      data={{
                        labels: sosByUnit.labels,
                        datasets: [
                          {
                            data: sosByUnit.values,
                            backgroundColor: sosUnitColors.map(c => withAlpha(c, 0.8)),
                            borderColor: sosUnitColors.map(c => withAlpha(c, 1)),
                            borderWidth: 1,
                          },
                        ],
                      }}
                      options={{
                        ...chartBaseOptions,
                        plugins: { ...chartBaseOptions.plugins, legend: { ...chartBaseOptions.plugins.legend, position: 'bottom' } },
                      }}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {/* Analytics v2 (P8): time-in-phase · report volume · SOS latency */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Time in phase per platoon (stacked) */}
        <div className="p-4 bg-command-panel border border-command-border rounded-xl">
          <h2 className="text-lg font-semibold text-gray-100 mb-3">Time in Phase per Platoon</h2>
          {timeInPhase.length > 0 ? (
            <div className="h-[220px]">
              <Bar
                data={phaseChart}
                options={{
                  ...chartBaseOptions,
                  plugins: { ...chartBaseOptions.plugins, legend: { ...chartBaseOptions.plugins.legend, position: 'bottom' } },
                  scales: {
                    x: { stacked: true, grid: { display: false }, ticks: { color: '#cbd5e1' } },
                    y: { stacked: true, grid: { color: 'rgba(148,163,184,0.12)' }, ticks: { color: '#94a3b8' }, title: { display: true, text: 'minutes', color: '#64748b' } },
                  },
                }}
              />
            </div>
          ) : (
            <p className="text-sm text-gray-500">{phaseFeedQ.isError ? 'Feed unavailable.' : 'No phase-tag events in range.'}</p>
          )}
        </div>

        {/* SOS response latency */}
        <div className="p-4 bg-command-panel border border-command-border rounded-xl">
          <h2 className="text-lg font-semibold text-gray-100 mb-3">SOS Response Times</h2>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="rounded-lg border border-command-border bg-command-bg p-3 text-center">
              <div className="text-[10px] text-gray-500">EVENTS</div>
              <div className="text-xl font-bold text-gray-100">{sosLatency.count}</div>
            </div>
            <div className="rounded-lg border border-command-border bg-command-bg p-3 text-center">
              <div className="text-[10px] text-gray-500">AVG ACK</div>
              <div className="text-xl font-bold text-command-info">{sosLatency.ackAvg.toFixed(1)}m</div>
            </div>
            <div className="rounded-lg border border-command-border bg-command-bg p-3 text-center">
              <div className="text-[10px] text-gray-500">AVG RESOLVE</div>
              <div className="text-xl font-bold text-command-success">{sosLatency.resolveAvg.toFixed(1)}m</div>
            </div>
          </div>
          {sosLatency.recent.length > 0 ? (
            <div className="h-[150px]">
              <Bar
                data={{
                  labels: sosLatency.recent.map((r) => r.callsign),
                  datasets: [{
                    label: 'Ack latency (min)',
                    data: sosLatency.recent.map((r) => Number(r.ack.toFixed(1))),
                    backgroundColor: withAlpha('#38bdf8', 0.75),
                    borderColor: '#38bdf8', borderWidth: 1, borderRadius: 6,
                  }],
                }}
                options={{
                  ...chartBaseOptions,
                  plugins: { ...chartBaseOptions.plugins, legend: { display: false } },
                  scales: {
                    x: { grid: { display: false }, ticks: { color: '#cbd5e1', font: { size: 10 } } },
                    y: { grid: { color: 'rgba(148,163,184,0.12)' }, ticks: { color: '#94a3b8' } },
                  },
                }}
              />
            </div>
          ) : (
            <p className="text-sm text-gray-500">{sosHistQ.isError ? 'SOS history unavailable.' : 'No SOS events in range.'}</p>
          )}
        </div>

        {/* Report volume by type */}
        <div className="p-4 bg-command-panel border border-command-border rounded-xl">
          <h2 className="text-lg font-semibold text-gray-100 mb-3">Report Volume by Type <span className="text-xs text-gray-500">({reportVolume.total})</span></h2>
          {reportVolume.total > 0 ? (
            <div className="h-[200px]">
              <Bar
                data={{
                  labels: REPORT_TYPES.filter((t) => (reportVolume.byType[t] ?? 0) > 0).map((t) => REPORT_TYPE_META[t].label),
                  datasets: [{
                    label: 'Reports',
                    data: REPORT_TYPES.filter((t) => (reportVolume.byType[t] ?? 0) > 0).map((t) => reportVolume.byType[t]),
                    backgroundColor: withAlpha('#a78bfa', 0.75), borderColor: '#a78bfa', borderWidth: 1, borderRadius: 6,
                  }],
                }}
                options={{
                  ...chartBaseOptions,
                  plugins: { ...chartBaseOptions.plugins, legend: { display: false } },
                  scales: {
                    x: { grid: { display: false }, ticks: { color: '#cbd5e1', font: { size: 10 } } },
                    y: { grid: { color: 'rgba(148,163,184,0.12)' }, ticks: { color: '#94a3b8' } },
                  },
                }}
              />
            </div>
          ) : (
            <p className="text-sm text-gray-500">{reportsQ.isError ? 'Reports unavailable.' : 'No reports in range.'}</p>
          )}
        </div>

        {/* Report volume by hour */}
        <div className="p-4 bg-command-panel border border-command-border rounded-xl">
          <h2 className="text-lg font-semibold text-gray-100 mb-3">Report Volume by Hour</h2>
          {reportVolume.total > 0 ? (
            <div className="h-[200px]">
              <Bar
                data={{
                  labels: Array.from({ length: 24 }, (_, h) => `${h}`),
                  datasets: [{
                    label: 'Reports',
                    data: reportVolume.byHour,
                    backgroundColor: withAlpha('#f59e0b', 0.7), borderColor: '#f59e0b', borderWidth: 1, borderRadius: 4,
                  }],
                }}
                options={{
                  ...chartBaseOptions,
                  plugins: { ...chartBaseOptions.plugins, legend: { display: false } },
                  scales: {
                    x: { grid: { display: false }, ticks: { color: '#94a3b8', font: { size: 9 } } },
                    y: { grid: { color: 'rgba(148,163,184,0.12)' }, ticks: { color: '#94a3b8', precision: 0 } },
                  },
                }}
              />
            </div>
          ) : (
            <p className="text-sm text-gray-500">{reportsQ.isError ? 'Reports unavailable.' : 'No reports in range.'}</p>
          )}
        </div>
      </div>

      {/* Movement (collapsed by default to keep one-screen compact) */}
      <details className="p-3 bg-command-panel border border-command-border rounded-xl" open={false}>
        <summary className="cursor-pointer list-none flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-100">Movement</h2>
          <span className="text-xs text-gray-500">expand</span>
        </summary>
        <div className="mt-3">
          <div className="flex items-center justify-end mb-2">
            <button
              onClick={() => handleExport('locations', 'csv')}
              className="flex items-center gap-1 text-sm text-command-accent hover:underline"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Export
            </button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
            <div className="rounded-xl border border-command-border bg-command-bg p-3">
              <div className="text-xs text-gray-400 mb-2">Moving vs Stationary</div>
              <div className="h-[180px]">
                <Doughnut
                  data={{
                    labels: movementSplit.labels,
                    datasets: [
                      {
                        data: movementSplit.values,
                        backgroundColor: [withAlpha('#22c55e', 0.8), withAlpha('#60a5fa', 0.6)],
                        borderColor: ['#22c55e', '#60a5fa'],
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    ...chartBaseOptions,
                    cutout: '60%',
                    plugins: {
                      ...chartBaseOptions.plugins,
                      legend: { ...chartBaseOptions.plugins.legend, position: 'bottom' },
                    },
                  }}
                />
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Total: {Math.round(movementTotalMin / 60)}h • Moving: {Math.round(movementMinutes.moving / 60)}h • Stationary: {Math.round(movementMinutes.stationary / 60)}h
              </div>
            </div>

            <div className="rounded-xl border border-command-border bg-command-bg p-3 lg:col-span-2">
              <div className="text-xs text-gray-400 mb-2">Distance & Speed</div>
              <div className="h-[180px]">
                <Bar
                  data={{
                    labels: ['Total Distance (km)', 'Avg Speed (km/h)'],
                    datasets: [
                      {
                        label: 'Value',
                        data: [
                          Number(movement?.summary?.totalDistanceKm || 0),
                          Number(movement?.summary?.avgSpeedKmh || 0),
                        ],
                        backgroundColor: [withAlpha('#38bdf8', 0.75), withAlpha('#f59e0b', 0.75)],
                        borderColor: ['#38bdf8', '#f59e0b'],
                        borderWidth: 1,
                        borderRadius: 10,
                      },
                    ],
                  }}
                  options={{
                    ...chartBaseOptions,
                    plugins: { ...chartBaseOptions.plugins, legend: { display: false } },
                    scales: {
                      x: { grid: { display: false }, ticks: { color: '#cbd5e1' } },
                      y: { grid: { color: 'rgba(148,163,184,0.12)' }, ticks: { color: '#94a3b8' } },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </details>

      {/* Export Options (collapsed by default to keep one-screen compact) */}
      <details className="p-3 bg-command-panel border border-command-border rounded-xl" open={false}>
        <summary className="cursor-pointer list-none flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-100">Export Data</h2>
          <span className="text-xs text-gray-500">expand</span>
        </summary>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
          {['locations', 'sos', 'geofence'].map((type) => (
            <div key={type} className="p-3 bg-command-bg rounded-lg border border-command-border">
              <p className="font-medium text-gray-200 mb-2 capitalize text-sm">{type}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleExport(type, 'csv')}
                  className="flex-1 px-3 py-1.5 bg-command-accent/10 text-command-accent text-sm rounded hover:bg-command-accent/20 transition-colors"
                >
                  CSV
                </button>
                <button
                  onClick={() => handleExport(type, 'json')}
                  className="flex-1 px-3 py-1.5 bg-command-accent/10 text-command-accent text-sm rounded hover:bg-command-accent/20 transition-colors"
                >
                  JSON
                </button>
              </div>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

